import { Close, DoneAll, Refresh } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, CircularProgress, FormControl, IconButton, Modal, Paper, SxProps, Typography } from "@mui/material"
import { Fragment, ReactNode, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux";
import { exactDigitCountValidationFn, greaterThanValueValidationFn, parseFalsyFields, phoneValidationFn } from '../utils/ValidationUtils';
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { EngineType, HubModel, OrderModel, OrderUploadLabels, PaymentMode } from "../utils/Models";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import { DateTime } from "luxon";
import { getVendorProfile } from "../../features/vendorProfile";

const MANDATORY_PICKUP_PHOTO_LABELS = [OrderUploadLabels.CUSTOMER, OrderUploadLabels.BIKE_LEFT, OrderUploadLabels.BIKE_RIGHT, OrderUploadLabels.BIKE_FRONT, OrderUploadLabels.BIKE_BACK, OrderUploadLabels.ODOMETER];

// Interfaces to store state and constants
interface LocalState {
  loading?: boolean;
  error?: string;
  rcData?: BikeRcData[];
  smsState?: 'PROGRESS' | 'DONE';
  emContactOtpState?: 'PROGRESS' | 'DONE';
  emContactOtpSendError?: string;
  otpVerifying?: boolean;
  otpVerified?: boolean;
  otpError?: string;
}
interface BikeRcData {
  odStart: number, rcNumber: string, lastServiced?: number, used?: boolean, conflictingOrder?: number, batteryNumber?: string
}
interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  otp?: string;
  emContact?: string;
  emContactOtp?: string;
  emContactVerified?: boolean;
  localAddress?: string;
  uploads: { [k in OrderUploadLabels]?: string },
  extraUploadNames: { [OrderUploadLabels.EXTRA_1]?: string, [OrderUploadLabels.EXTRA_2]?: string },
  extraUploadCount: number;
  rcData: BikeRcData[];
  errors: { emContact?: string, localAddress?: string, otp?: string, emContactOtp?: string };
}

const STEP_LABELS = ['Overview', 'Vehicle Details', 'Booking Details', 'Uploads'];
const TOTAL_STEPS = STEP_LABELS.length;

// Start booking component
export function StartBooking(props: {
  children: ReactNode,
  order: OrderModel,
  style: any,
  hub: HubModel
  backFn: () => void
}) {

  // Refs and consts
  const profile = useSelector(getVendorProfile);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const bikeCount = props.order?.bikes?.[0]?.order_bikes?.quantity || 0;
  const checkboxOptions = [
    'Got the documentation done',
    ...(props.order.paymentMode !== PaymentMode.ONLINE ? [`Collected remaining rent (₹${props.order.offlineCashAmount.toFixed(2)})`] : []),
    ...(props.order.depositAmount > 0.001 ? [`Collected security deposit (₹${props.order.depositAmount.toFixed(2)})`] : []),
    'Provided helmet to the customer',
    'Provided vehicle documents to the customer',
    'Took video of the vehicle',
    'Asked customer to take a video of the vehicle',
    'Clicked photo of the customer with the vehicle'
  ];

  // Local state
  const [localState, setLocalState] = useState<LocalState>({});
  const [formState, setFormState] = useState<FormState>({
    errors: {},
    uploads: {},
    extraUploadNames: {},
    extraUploadCount: 0,
    rcData: Array(bikeCount).fill(0).map((_, i) => {
      const existing = props.order.bikeDetails?.[i];
      return { odStart: existing?.odStart || 0, rcNumber: existing?.rcNumber || '', batteryNumber: existing?.batteryNumber } as BikeRcData;
    })
  });
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const handleOpen = () => { setOpen(true); setCurrentStep(1); };
  const handleClose = () => setOpen(false);

  // Parse state or props
  const authCookie = getAuthCookie();
  const allBikeRcData = localState.rcData || [];
  const validRcSelectOptions = allBikeRcData.map(rc => ({ value: rc.rcNumber, display: rc.conflictingOrder ? `${rc.rcNumber} (Ongoing Order ID: ${rc.conflictingOrder})` : rc.rcNumber, invisible: rc.used }));

  // Parse state to decide submit button state
  const emptyFields = parseFalsyFields([
    { name: 'OTP', value: formState.otp },
    ...(Array(bikeCount).fill(0).map((_, i) => ({ name: `RC number ${i + 1}`, value: formState?.rcData?.[i].rcNumber }))),
    ...(Array(bikeCount).fill(0).map((_, i) => ({ name: `Odometer reading ${i + 1}`, value: formState?.rcData?.[i].odStart })))
  ]);
  const emContactTenDigits = !!(formState.emContact && String(formState.emContact).replace(/\D/g, '').length === 10);

  // Per-step proceed gating
  const step2CanProceed = Array(bikeCount).fill(0).every((_, i) =>
    !!(formState.rcData?.[i]?.rcNumber) && (formState.rcData?.[i]?.odStart > 0)
  );
  const step3OtpReady = !!(formState.otp && formState.otp.length === 4 && !formState.errors.otp);
  const emContactMandatoryOk = !profile.mandatoryEmContact || !!formState.emContactVerified;
  const step3CanProceed = !!(step3OtpReady && localState.otpVerified && emContactMandatoryOk);
  const missingMandatoryPhotoLabels = profile.mandatoryPickupPhotos
    ? MANDATORY_PICKUP_PHOTO_LABELS.filter(label => !props.order.imageData?.[label] && !formState.uploads[label])
    : [];
  const step4CanProceed = missingMandatoryPhotoLabels.length === 0;

  let disableSubmit = false;
  if (
    emptyFields.length ||
    formState.errors.otp ||
    formState.errors.emContact ||
    formState.errors.localAddress ||
    !step4CanProceed ||
    !emContactMandatoryOk
  ) {
    disableSubmit = true;
  }

  function canProceedFromStep(step: number): boolean {
    if (step === 1) return true;
    if (step === 2) return step2CanProceed;
    if (step === 3) return step3CanProceed;
    if (step === 4) return step4CanProceed;
    return true;
  }

  // Load data when component opens
  useEffect(() => {
    if (localState.rcData || !open) return;
    setLocalState({ loading: true });
    (async () => {
      try {
        if (!props.order.bikes || !props.order.bikes.length || !props.order.bikes[0].id) {
          throw new Error('Failed to load bike data!');
        }
        const { data } = await GobikesApi.get(`/vendors/hubs/${props.order.hubId}/bikes/${props.order.bikes[0].id}`, {
          params: { loadRcNumbersInUse: true, startTime: props.order.rideStartScheduled, endTime: props.order.rideEndScheduled },
          headers: { 'vendor-token': authCookie }
        });
        if (!data.data || !data.data.length || !data.data[0].bikes) {
          throw new Error('Failed to load RC numbers data!');
        }
        const rcData = data.data[0].bikes;
        const rcOrderMap: { [k: string]: number[] } = {};
        if (data.data[0].bike && data.data[0].bike.orders) {
          for (const order of data.data[0].bike.orders) {
            if (order.bikeDetails && order.bikeDetails.length) {
              for (const bikeDetail of order.bikeDetails) {
                if (!rcOrderMap[bikeDetail.rcNumber]) rcOrderMap[bikeDetail.rcNumber] = [];
                rcOrderMap[bikeDetail.rcNumber].push(order.id);
              }
            }
          }
        }
        for (const rcDatum of rcData) {
          if (rcOrderMap[rcDatum.rcNumber]) {
            rcDatum.conflictingOrder = rcOrderMap[rcDatum.rcNumber][0];
          }
        }
        setLocalState({ loading: false, rcData });
      } catch (e: any) {
        setLocalState({ loading: false, error: e.message });
      }
    })();
  }, [props, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle  form submission
  async function submitStartOrderRequest(e: any) {

    // Parse input and check for errors
    e.preventDefault();
    if (emptyFields.length || formState.errors.otp || formState.errors.emContact || formState.errors.localAddress) {
      setFormState({ ...formState, error: true, loading: false, message: 'Please fix all errors or missing fields' });
    }

    // Generate upload data
    const images = []
    for (const label of Object.keys(formState.uploads)) {
      const data = formState.uploads[label as OrderUploadLabels];
      const isExtra = label === OrderUploadLabels.EXTRA_1 || label === OrderUploadLabels.EXTRA_2;
      const customLabel = isExtra ? formState.extraUploadNames[label as OrderUploadLabels.EXTRA_1 | OrderUploadLabels.EXTRA_2] : undefined;
      if (data && (!isExtra || customLabel))
        images.push({ label, data, ...(customLabel && { customLabel }) })
    }

    // Set loading mode and try to start the booking
    setFormState({ ...formState, loading: true });
    const payload = { otp: formState.otp, bikeData: formState.rcData, emPhone: formState.emContact, localAddress: formState.localAddress, ...(images.length && { images }) };
    try {
      await GobikesApi.put(`/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/start`, payload, { headers: { 'vendor-token': authCookie } });
      setFormState({ ...formState, error: false, message: 'Order started successfully!', loading: false });
      setTimeout(() => { props.backFn() }, 2000);
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setFormState({ ...formState, error: true, message: errorMessage, loading: false })
    }
  }

  // Return JSX
  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>
      <Modal
        open={open}
        className="gbk-modal"
        onClose={handleClose}
      >
        <Box className="gbk-modal-content">
          {localState.loading || localState.error ? renderLoading() : renderStartOrderForm()}
        </Box>
      </Modal >
    </Fragment>
  )

  // Render loading
  function renderLoading() {
    return <Paper className='gbk-paper'>
      <div className="loader">
        {localState.loading ? <CircularProgress /> : ''}
        <div className={`gbk-message ${localState.error ? 'error' : ''}`}>
          {localState.error}
        </div>
      </div>
    </Paper>
  }

  // Step indicator
  function renderStepIndicator() {
    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px 20px 6px', borderBottom: '1px solid #eee' }}>
        {STEP_LABELS.map((label, i) => (
          <Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                backgroundColor: currentStep === i + 1 ? '#558b2f' : currentStep > i + 1 ? '#8bc34a' : '#e0e0e0',
                color: currentStep >= i + 1 ? 'white' : '#999',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 'bold'
              }}>
                {i + 1}
              </div>
              <span style={{ fontSize: '10px', marginTop: 2, color: currentStep === i + 1 ? '#558b2f' : '#999', whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                flex: 1, height: 2,
                backgroundColor: currentStep > i + 1 ? '#8bc34a' : '#e0e0e0',
                margin: '0 4px', marginBottom: 14
              }} />
            )}
          </Fragment>
        ))}
      </div>
    );
  }

  // Step 1: Pickup Process + Charges and Limits
  function renderStep1() {
    return (
      <FormControl sx={{ margin: { xs: '12px 16px', md: '20px 30px' }, width: { xs: '92%', md: '80%' }, display: 'block' }}>
        <Typography sx={{ fontSize: { xs: '13px', md: '16px' }, paddingBottom: '4px' }}><b>Pickup Process</b></Typography>
        <ul className="roboto gbk-list" style={{ fontSize: '13px', margin: '4px 0' }}>
          {checkboxOptions.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
        <br />
        <Typography sx={{ fontSize: { xs: '13px', md: '16px' } }}><b>Charges and Limits</b></Typography>
        <div style={{ paddingRight: '10px' }}>
          <ul className="roboto gbk-list" style={{ marginTop: '6px', fontSize: '13px' }}>
            <li>
              Distance limit:
              {props.order.staticData?.distanceLimit
                ? <span> {props.order.staticData?.distanceLimit > 0
                  ? `${props.order.staticData?.distanceLimit} Km`
                  : 'No limit'}</span>
                : ' Failed to load'}
            </li>
            {
              props.order.staticData?.distanceLimit && props.order.staticData?.distanceLimit > 0
                ? (<li>
                  Excess charges:
                  {props.order.staticData?.penalty?.distance ? <span> <span className="roboto">₹</span>{props.order.staticData.penalty.distance} / Km</span> : ' Failed to load'}
                </li>) : ''
            }
            <li>
              Late Penalty:
              {props.order.staticData?.penalty?.late
                ? <span> <span className="roboto">₹</span>{props.order.staticData.penalty.late} / Hour</span>
                : ' No penaly'}
            </li>
          </ul>
        </div>
      </FormControl>
    );
  }

  // Step 2: Vehicle Details
  function renderStep2() {
    return (
      <FormControl sx={{ margin: { xs: '12px 16px', md: '20px 30px' }, width: { xs: '92%', md: '80%' }, display: 'block' }}>
        <Typography sx={{ fontSize: { xs: '13px', md: '16px' } }}><b>Vehicle Details</b></Typography>
        <br />
        {Array(bikeCount).fill(0).map((_, i) => (
          <div key={i} className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
            <GobikesField
              type={GbkFieldType.SELECT}
              label={`Select Vehicle Number ${i + 1}`}
              editMode={true}
              value={formState?.rcData?.[i].rcNumber}
              selectOptions={validRcSelectOptions}
              updateFn={(v: string) => {
                const newFormRcData = formState.rcData || [];
                const newRcData = localState.rcData?.map(d => {
                  if (d.rcNumber === formState?.rcData?.[i].rcNumber) d.used = false;
                  if (d.rcNumber === v) d.used = true;
                  return d;
                });
                setLocalState({ ...localState, rcData: newRcData })
                newFormRcData[i].rcNumber = v;
                setFormState({ ...formState, rcData: newFormRcData });
              }}
              useInternalLabel={true}
            />
            <span className="inb pad-5 error-text fs-16">*</span>
            <br /><br /><br />
            <GobikesField
              type={GbkFieldType.NUMBER}
              label={`Start Odometer Reading ${i + 1}`}
              editMode={true}
              value={formState?.rcData?.[i].odStart || ''}
              validateFn={greaterThanValueValidationFn(0)}
              updateFn={(v: number) => {
                const newRcData = formState.rcData || [];
                newRcData[i].odStart = v;
                setFormState({ ...formState, rcData: newRcData })
              }}
              useInternalLabel={true}
            />
            <span className="inb pad-5 error-text fs-16">*</span>
            <br /><br />
            {props.order.bikes?.[0]?.engineType === EngineType.E_BIKE && props.order.bikes?.[0]?.removableBattery && (
              <>
                <GobikesField
                  type={GbkFieldType.STRING}
                  label={`Battery Number ${i + 1}`}
                  editMode={true}
                  useInternalLabel={true}
                  value={formState?.rcData?.[i].batteryNumber || ''}
                  updateFn={(v: string) => {
                    const newRcData = formState.rcData || [];
                    newRcData[i].batteryNumber = v;
                    setFormState({ ...formState, rcData: newRcData });
                  }}
                />
                <br /><br />
              </>
            )}
            <GobikesField
              type={GbkFieldType.DATE}
              label={`Last Service Date ${i + 1}`}
              editMode={true}
              value={formState?.rcData?.[i].lastServiced ? DateTime.fromMillis(formState?.rcData?.[i].lastServiced || 0) : null}
              updateFn={(v: number) => {
                const newRcData = formState.rcData || [];
                newRcData[i].lastServiced = DateTime.fromMillis(v).startOf('hour').toMillis();
                setFormState({ ...formState, rcData: newRcData })
              }}
            />
          </div>
        ))}
      </FormControl>
    );
  }

  // Step 3: Booking Details
  function renderStep3() {
    return (
      <FormControl sx={{ margin: { xs: '12px 16px', md: '20px 30px' }, width: { xs: '92%', md: '80%' }, display: 'block' }}>
        <Typography sx={{ fontSize: { xs: '13px', md: '16px' } }}><b>Booking Details</b></Typography>
        <br />

        {/* Emergency Contact: field + Send OTP button in a row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: 'calc(100% - 10px)' }}>
          <div style={{ flex: 1 }}>
            <GobikesField
              type={GbkFieldType.NUMBER}
              label={'Emergency Contact' + (profile.mandatoryEmContact ? ' *' : '')}
              editMode={true}
              useInternalLabel={true}
              value={formState.emContact || ''}
              validateFn={phoneValidationFn(!profile.mandatoryEmContact)}
              updateFn={(v: string, error?: string) => {
                const val = (v === null || v === undefined || String(v) === '0') ? '' : String(v).trim();
                const primaryPhone = String(props.order.user?.phone || '').trim();
                const sameAsPrimary = val && primaryPhone && val === primaryPhone;
                setFormState({
                  ...formState,
                  emContact: val,
                  emContactOtp: undefined,
                  emContactVerified: false,
                  errors: {
                    ...formState.errors,
                    emContact: sameAsPrimary
                      ? 'Number already registered, try another emergency number'
                      : (v ? error : undefined),
                    emContactOtp: undefined
                  }
                });
                setLocalState({ ...localState, emContactOtpState: undefined, emContactOtpSendError: undefined });
              }}
            />
          </div>
          {emContactTenDigits && !formState.errors.emContact && (
            <div style={{ paddingTop: '8px', flexShrink: 0 }}>
              <LoadingButton
                startIcon={formState.emContactVerified ? <DoneAll /> : <Refresh />}
                variant={'outlined'}
                className="thick-border"
                size="small"
                loading={localState.emContactOtpState === 'PROGRESS'}
                disabled={formState.emContactVerified || localState.emContactOtpState === 'DONE'}
                onClick={async () => {
                  setLocalState({ ...localState, emContactOtpState: 'PROGRESS', emContactOtpSendError: undefined });
                  try {
                    await GobikesApi.post(
                      `/vendors/hubs/${props.hub.id}/orders/${props.order.id}/send-em-contact-otp`,
                      { phone: formState.emContact },
                      { headers: { 'vendor-token': authCookie } }
                    );
                    setLocalState({ ...localState, emContactOtpState: 'DONE' });
                  } catch (e: any) {
                    const status = e.response?.status;
                    const msg = (status === 404 || status === 500 || !e.response)
                      ? 'Failed to send OTP. Please try again.'
                      : (e.response?.data?.message || 'Failed to send OTP. Please try again.');
                    setLocalState({ ...localState, emContactOtpState: undefined, emContactOtpSendError: msg });
                  }
                }}
              >
                {formState.emContactVerified ? 'Verified' : localState.emContactOtpState === 'DONE' ? 'OTP Sent' : 'Send OTP'}
              </LoadingButton>
            </div>
          )}
        </div>
        {formState.errors.emContact === 'Number already registered, try another emergency number' && (
          <div className="error-text fs-14" style={{ marginTop: '-4px', marginBottom: '4px' }}>
            {formState.errors.emContact}
          </div>
        )}
        {localState.emContactOtpSendError && (
          <div className="error-text fs-14" style={{ marginTop: '-4px', marginBottom: '4px' }}>
            {localState.emContactOtpSendError}
          </div>
        )}
        {localState.emContactOtpState === 'DONE' && !formState.emContactVerified && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: 'calc(100% - 10px)', marginTop: '8px' }}>
            <div style={{ flex: 1 }}>
              <GobikesField
                type={GbkFieldType.STRING}
                label={'Emergency Contact OTP'}
                editMode={true}
                value={formState.emContactOtp || ''}
                validateFn={exactDigitCountValidationFn(4)}
                updateFn={(v: string, error?: string) => {
                  setFormState({ ...formState, emContactOtp: v.replace(/[^0-9]/g, ''), errors: { ...formState.errors, emContactOtp: error } });
                }}
                useInternalLabel={true}
              />
              {formState.errors.emContactOtp && (
                <div className="error-text fs-14" style={{ marginTop: '4px' }}>{formState.errors.emContactOtp}</div>
              )}
            </div>
            <div style={{ paddingTop: '8px', flexShrink: 0 }}>
              <LoadingButton
                variant={'contained'}
                size="small"
                disabled={!formState.emContactOtp || !!formState.errors.emContactOtp}
                onClick={async () => {
                  try {
                    await GobikesApi.post(
                      `/vendors/hubs/${props.hub.id}/orders/${props.order.id}/verify-em-contact-otp`,
                      { phone: formState.emContact, otp: formState.emContactOtp },
                      { headers: { 'vendor-token': authCookie } }
                    );
                    setFormState({ ...formState, emContactVerified: true, errors: { ...formState.errors, emContactOtp: undefined } });
                  } catch (e: any) {
                    const status = e.response?.status;
                    const msg = (status === 404 || status === 500 || !e.response)
                      ? 'Verification failed. Please try again.'
                      : (e.response?.data?.message || 'Invalid OTP');
                    setFormState({ ...formState, errors: { ...formState.errors, emContactOtp: msg } });
                  }
                }}
              >
                Verify OTP
              </LoadingButton>
            </div>
          </div>
        )}
        <br />
        <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
          <GobikesField
            type={GbkFieldType.STRING}
            label={'Local Address'}
            editMode={true}
            useInternalLabel={true}
            value={formState.localAddress || ''}
            updateFn={(v: string, error?: string) => {
              setFormState({ ...formState, localAddress: v, errors: { ...formState.errors, localAddress: error } })
            }}
          />
        </div>
        <br />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: 'calc(100% - 10px)' }}>
          <div style={{ flex: 1 }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Pickup OTP'}
              editMode={true}
              value={formState.otp || ''}
              validateFn={exactDigitCountValidationFn(4)}
              updateFn={(v: string, error?: string) => {
                setFormState({ ...formState, otp: v.replace(/[^0-9]/g, ''), errors: { ...formState.errors, otp: error } });
                setLocalState({ ...localState, otpVerified: false, otpError: undefined });
              }}
              useInternalLabel={true}
            />
            {localState.otpError && <div className="error-text fs-14" style={{ marginTop: '4px' }}>{localState.otpError}</div>}
          </div>
          <div style={{ paddingTop: '8px', flexShrink: 0 }}>
            <LoadingButton
              startIcon={localState.otpVerified ? <DoneAll /> : undefined}
              variant={localState.otpVerified ? 'contained' : 'outlined'}
              className="thick-border"
              size="small"
              loading={localState.otpVerifying}
              disabled={!step3OtpReady || localState.otpVerified}
              onClick={async () => {
                setLocalState({ ...localState, otpVerifying: true, otpError: undefined });
                try {
                  await GobikesApi.post(
                    `/vendors/hubs/${props.hub.id}/orders/${props.order.id}/verify-pickup-otp`,
                    { otp: formState.otp },
                    { headers: { 'vendor-token': authCookie } }
                  );
                  setLocalState({ ...localState, otpVerifying: false, otpVerified: true, otpError: undefined });
                } catch (e: any) {
                  const msg = e.response?.data?.message || 'Invalid OTP';
                  setLocalState({ ...localState, otpVerifying: false, otpVerified: false, otpError: msg });
                }
              }}
            >
              {localState.otpVerified ? 'Verified' : 'Verify'}
            </LoadingButton>
          </div>
          <span className="inb pad-5 error-text fs-16">*</span>
        </div>
        {/* Resend OTP */}
        <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
          <div className="gbk-field" style={{ marginTop: 0 }}>
            <LoadingButton
              startIcon={localState.smsState === 'DONE' ? <DoneAll /> : <Refresh />}
              variant={'outlined'}
              className="thick-border"
              loading={localState.smsState === 'PROGRESS'}
              disabled={localState.smsState === 'DONE'}
              onClick={async () => {
                setLocalState({ smsState: 'PROGRESS' });
                try {
                  await GobikesApi.post(`/vendors/hubs/${props.hub.id}/orders/${props.order.id}/resend-pickup-otp-sms`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                  setLocalState({ smsState: 'DONE' })
                } catch (e: any) {
                  const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                  setLocalState({ error: errorMessage, smsState: 'DONE' })
                }
              }}
            >
              {localState.smsState === 'DONE' ? 'OTP Sent Successfully' : 'Resend OTP'}
            </LoadingButton>
          </div>
        </div>
      </FormControl>
    );
  }

  // Step 4: Uploads
  function renderStep4() {
    const uploadFieldSx: SxProps = {
      width: { xs: '100%', md: '200px' },
      marginBottom: { xs: '8px', md: '20px' }
    }
    return (
      <FormControl sx={{ margin: { xs: '12px 16px', md: '20px 30px' }, width: { xs: '92%', md: '80%' }, display: 'block' }}>
        <Typography sx={{ fontSize: { xs: '13px', md: '16px' } }}><b>Uploads</b></Typography>
        {profile.mandatoryPickupPhotos && missingMandatoryPhotoLabels.length > 0 && (
          <div className="error-text fs-14" style={{ marginTop: '4px' }}>Photos marked with * are required before starting the ride</div>
        )}
        <Box sx={{ display: { xs: 'grid', md: 'block' }, gridTemplateColumns: '1fr 1fr', gap: '8px', mt: 1 }}>
          <GobikesField label={`Customer Photo${missingMandatoryPhotoLabels.includes(OrderUploadLabels.CUSTOMER) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} allowCamera tag={OrderUploadLabels.CUSTOMER} gbkFieldSx={uploadFieldSx} editMode={true} value={formState.uploads.CUSTOMER || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.CUSTOMER]: v } })} />
          <GobikesField label={`Vehicle Left${missingMandatoryPhotoLabels.includes(OrderUploadLabels.BIKE_LEFT) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} allowCamera tag={OrderUploadLabels.BIKE_LEFT} gbkFieldSx={uploadFieldSx} editMode={true} value={formState.uploads.BIKE_LEFT || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_LEFT]: v } })} />
          <GobikesField label={`Vehicle Right${missingMandatoryPhotoLabels.includes(OrderUploadLabels.BIKE_RIGHT) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} allowCamera tag={OrderUploadLabels.BIKE_RIGHT} gbkFieldSx={uploadFieldSx} editMode={true} value={formState.uploads.BIKE_RIGHT || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_RIGHT]: v } })} />
          <GobikesField label={`Vehicle Front${missingMandatoryPhotoLabels.includes(OrderUploadLabels.BIKE_FRONT) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} allowCamera tag={OrderUploadLabels.BIKE_FRONT} gbkFieldSx={uploadFieldSx} editMode={true} value={formState.uploads.BIKE_FRONT || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_FRONT]: v } })} />
          <GobikesField label={`Vehicle Back${missingMandatoryPhotoLabels.includes(OrderUploadLabels.BIKE_BACK) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} allowCamera tag={OrderUploadLabels.BIKE_BACK} gbkFieldSx={uploadFieldSx} editMode={true} value={formState.uploads.BIKE_BACK || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_BACK]: v } })} />
          <GobikesField label={`Odometer${missingMandatoryPhotoLabels.includes(OrderUploadLabels.ODOMETER) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} allowCamera tag={OrderUploadLabels.ODOMETER} gbkFieldSx={uploadFieldSx} editMode={true} value={formState.uploads.ODOMETER || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.ODOMETER]: v } })} />
          <GobikesField label="Other" type={GbkFieldType.UPLOAD} allowCamera tag={OrderUploadLabels.OTHER} gbkFieldSx={uploadFieldSx} editMode={true} value={formState.uploads.OTHER || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.OTHER]: v } })} />
          {([OrderUploadLabels.EXTRA_1, OrderUploadLabels.EXTRA_2] as const).slice(0, formState.extraUploadCount).map(label => (
            <Box key={label} sx={{ ...uploadFieldSx, display: { xs: 'flex', md: 'inline-flex' }, flexDirection: { xs: 'row', md: 'column' }, alignItems: { xs: 'flex-start', md: 'stretch' }, gap: '8px', gridColumn: { xs: '1 / -1', md: 'auto' }, verticalAlign: 'top', width: { xs: '100%', md: '200px' }, mr: { md: '20px' } }}>
              <GobikesField
                label="Upload Name"
                type={GbkFieldType.STRING}
                gbkFieldSx={{ flex: 1, width: '100% !important' }}
                editMode={true}
                useInternalLabel={true}
                value={formState.extraUploadNames[label] || ''}
                updateFn={(v: string) => setFormState({ ...formState, extraUploadNames: { ...formState.extraUploadNames, [label]: v } })}
              />
              <GobikesField label="Upload" type={GbkFieldType.UPLOAD} allowCamera tag={label} gbkFieldSx={{ flexShrink: 0 }} editMode={true} value={formState.uploads[label] || ''} updateFn={v => setFormState({ ...formState, uploads: { ...formState.uploads, [label]: v } })} />
            </Box>
          ))}
        </Box>
        {formState.extraUploadCount < 2 && (
          <Button
            variant="outlined"
            className="no-text-transform"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => setFormState({ ...formState, extraUploadCount: formState.extraUploadCount + 1 })}
          >
            + Add Extra Upload
          </Button>
        )}
        <div className={`gbk-login-message ${formState.error ? 'error' : ''}`}>{formState.message}</div>
      </FormControl>
    );
  }

  // Render start order form
  function renderStartOrderForm() {
    return <Paper className='gbk-paper'>

      {/* Header */}
      <div className="header">
        <Typography className="header-left"><b>Pickup</b></Typography>
        <IconButton className="header-right" onClick={handleClose} style={{ padding: 0 }}>
          <Close />
        </IconButton>
      </div>

      {/* Step indicator */}
      {renderStepIndicator()}

      {/* Step content */}
      <div className="content pickup-form">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      {/* Footer: Back + Next / Start Ride */}
      <div className="footer">
        <div style={{ display: 'flex', gap: '10px', padding: '0 10px' }}>
          {currentStep > 1 && (
            <Button
              variant="outlined"
              className="no-text-transform"
              sx={{ flex: 1 }}
              onClick={() => setCurrentStep(s => s - 1)}
            >
              Back
            </Button>
          )}
          {currentStep < TOTAL_STEPS ? (
            <Button
              variant="contained"
              className="no-text-transform"
              sx={{ flex: 1 }}
              disabled={!canProceedFromStep(currentStep)}
              onClick={() => setCurrentStep(s => s + 1)}
            >
              Next
            </Button>
          ) : (
            <form onSubmit={submitStartOrderRequest} style={{ flex: 1 }}>
              <LoadingButton
                variant='contained'
                type='submit'
                ref={submitButtonRef}
                loading={formState.loading}
                className="no-text-transform"
                sx={{ width: '100%' }}
                disabled={disableSubmit}
              >
                Start Ride
              </LoadingButton>
            </form>
          )}
        </div>
      </div>
    </Paper>
  }

}
